import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  CircularProgress,
  Alert,
  IconButton,
  Breadcrumbs,
  Link,
  Switch,
  FormControlLabel,
} from "@mui/material";
import { CloudUpload, Save, ArrowBack, Delete } from "@mui/icons-material";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { createSubCategory, updateSubCategory } from "../../services/Api";

const ModifySubCategories = () => {
  const { categoryId, id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const isEdit = !!id;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [orderError, setOrderError] = useState("");

  const [formData, setFormData] = useState({
    name: "",
    order: "1",
    isActive: true,
    category: categoryId,
  });
  const [image, setImage] = useState(null);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    if (isEdit && location.state?.subCategory) {
      const { subCategory } = location.state;
      setFormData({
        name: subCategory.name || "",
        order: subCategory.order || "1",
        isActive:
          subCategory.isActive !== undefined ? subCategory.isActive : true,
        category: categoryId,
      });
      setPreview(subCategory.image);
    }
  }, [isEdit, location.state, categoryId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let finalValue = value;

    if (name === "order") {
      if (value === "") {
        finalValue = "";
      } else if (String(value).match(/\.\d+/)) {
        // If it has a decimal point followed by digits (e.g., '1.5'), round it
        finalValue = String(Math.round(parseFloat(value)));
      } else {
        // Allow digits and the decimal point, automatically stripping '-' signs
        finalValue = String(value).replace(/[^0-9.]/g, "");
      }

      // Real-time bounds validation
      if (finalValue !== "" && (Number(finalValue) < 1 || Number(finalValue) > 100)) {
        setOrderError("Display Order must be between 1 and 100");
      } else {
        setOrderError("");
      }
    }

    setFormData((prev) => ({ ...prev, [name]: finalValue }));
  };

  const handleSwitchChange = (e) => {
    setFormData((prev) => ({ ...prev, isActive: e.target.checked }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImage(file);
      setPreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(false);

    const subCategoryName = formData.name ? formData.name.trim() : "";
    if (!subCategoryName) {
      setError("Subcategory name is required");
      setLoading(false);
      return;
    }

    const orderVal = formData.order;
    if (!/^\d+$/.test(String(orderVal).trim()) || Number(orderVal) < 1) {
      setError("Display Order must be a valid positive integer (minimum 1).");
      setLoading(false);
      return;
    }

    const data = new FormData();
    data.append("name", formData.name);
    data.append("order", formData.order);
    data.append("isActive", formData.isActive);
    data.append("categoryId", categoryId);
    if (image) {
      data.append("subCategoryImages", image);
    }

    try {
      let response;
      if (isEdit) {
        response = await updateSubCategory(id, data);
      } else {
        response = await createSubCategory(data);
      }

      if (response.success) {
        setSuccess(true);
        setTimeout(() => navigate(`/categories/${categoryId}/items`), 1500);
      } else {
        setError(response.message || "Failed to save subcategory");
      }
    } catch (err) {
      setError(err.response?.data?.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 1 }}>
          <Link
            underline="hover"
            color="inherit"
            href="#"
            onClick={(e) => {
              e.preventDefault();
              navigate(`/categories/${categoryId}/items`);
            }}
            sx={{ display: "flex", alignItems: "center" }}
          >
            <ArrowBack sx={{ mr: 0.5, fontSize: "inherit" }} />
            Subcategories
          </Link>
          <Typography color="text.primary">
            {isEdit ? "Edit Subcategory" : "Create Subcategory"}
          </Typography>
        </Breadcrumbs>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          {isEdit ? "Modify Subcategory" : "Create New Subcategory"}
        </Typography>
      </Box>

      <Paper
        elevation={0}
        sx={{
          p: 4,
          borderRadius: 2,
          border: "1px solid",
          borderColor: "text.border",
        }}
      >
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            Subcategory saved successfully! Redirecting...
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Grid container spacing={4}>
            {/* Image Upload Section */}
            <Grid size={{ xs: 12, md: 4 }}>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                Subcategory Image
              </Typography>
              <Box
                sx={{
                  border: "2px dashed",
                  borderColor: "text.border",
                  borderRadius: 2,
                  height: 250,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  overflow: "hidden",
                  backgroundColor: "rgba(0,0,0,0.02)",
                  position: "relative",
                }}
              >
                {preview ? (
                  <>
                    <Box
                      component="img"
                      src={preview}
                      sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                    <IconButton
                      sx={{
                        position: "absolute",
                        top: 8,
                        right: 8,
                        backgroundColor: "rgba(255,255,255,0.8)",
                        "&:hover": { backgroundColor: "white" },
                      }}
                      onClick={() => {
                        setImage(null);
                        setPreview(null);
                      }}
                    >
                      <Delete color="error" />
                    </IconButton>
                  </>
                ) : (
                  <>
                    <CloudUpload
                      sx={{ fontSize: 48, color: "text.secondary", mb: 1 }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      Upload Subcategory Image
                    </Typography>
                  </>
                )}
                <input
                  id="file-input-subcategory"
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={(e) => {
                    handleImageChange(e);
                    e.target.value = null;
                  }}
                />
              </Box>
                <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mt: 1, textAlign: "center" }}
              >
                Note: Image size must be 300px * 300px
              </Typography>
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <Button
                  variant="outlined"
                  sx={{ mt: 2, fontSize: "10px !important" }}
                  startIcon={<CloudUpload />}
                  onClick={() =>
                    document.getElementById("file-input-subcategory").click()
                  }
                >
                  Upload Image
                </Button>
              </Box>
            
            </Grid>

            {/* Details Section */}
            <Grid size={{ xs: 12, md: 8 }}>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                Subcategory Details
              </Typography>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 12 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Subcategory Name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    placeholder="e.g. Milk & Dairy"
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label="Display Order"
                    name="order"
                    value={formData.order}
                    onChange={handleInputChange}
                    required
                    placeholder="1"
                    inputProps={{ min: 1, step: "1" }}
                    error={Boolean(orderError)}
                    helperText={orderError}
                  />
                  <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "flex", mt: 1, textAlign: "start", flexDirection:"column",justifyContent: "flex-start" }}
              >
                Display Order must be between 1 and 100
              </Typography>
                </Grid>
                
                <Grid size={{ xs: 12, md: 6 }}>
                  <Box
                    sx={{
                      height: "100%",
                      display: "flex",
                      alignItems: "center",
                      ml: 1,
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.isActive}
                          onChange={handleSwitchChange}
                          color="primary"
                        />
                      }
                      label={
                        formData.isActive ? "Active on App" : "Hidden from App"
                      }
                    />
                  </Box>
                </Grid>
              </Grid>

              <Box
                sx={{
                  mt: 5,
                  display: "flex",
                  gap: 2,
                  justifyContent: "flex-end",
                }}
              >
                <Button
                  variant="outlined"
                  onClick={() => navigate(`/categories/${categoryId}/items`)}
                  disabled={loading}
                  sx={{ px: 4 }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={
                    loading ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      <Save />
                    )
                  }
                  disabled={loading || (!isEdit && !image)}
                  sx={{ px: 6, backgroundColor: "primary.main" }}
                >
                  {isEdit ? "Update Subcategory" : "Create Subcategory"}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Box>
  );
};

export default ModifySubCategories;
